import { useMeasurementUnit } from 'hooks/useMeasurementUnit';
import { CalculatorOutlined } from '@ant-design/icons';
import { useDebounceFn, useMemoizedFn, useSize } from 'ahooks';
import {
  DatePicker,
  Form,
  Modal,
  Popconfirm,
  Popover,
  Select,
  Tooltip,
  Typography,
} from 'antd';
import { routesMap } from 'app/Router';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { isIndustryAvgOrg } from 'domains/orgDomain';
import { RevxButton } from 'components/RevxButton';
import {
  DEFAULT_DATE_FORMAT,
  RFQ_SHIPPING_DATE_FORMAT_LOCAL,
  getFormatDate,
} from 'components/RevxDate';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { RevxInputNumber } from 'components/RevxInputNumber';
import RevxSupplierShippingMethodTable from 'components/RevxSupplierShippingMethodTable';
import { UnderlineText } from 'components/RevxTypography';
import { CartLineItem } from 'domains/cartDomain';
import {
  AdjustMarkupPayload,
  JobComparatorPart,
  JobComparatorShipping,
  JobComparatorShippingCarrierServices,
  JobComparatorSvc,
  JobEstimator,
  JobEstimatorComparatorItem,
  JobEstimatorConfirmPaperUpdateParamsColumnType,
  JobRFQAdjustPayload,
  JobSpec,
  PopoverType,
  SearchScopeEnum,
  ShippingAccountTypeEnum,
} from 'domains/jobconfigDomain';
import { useJobRevision } from 'hooks/useJobRevision';
import { useOrg, useOrgId } from 'hooks/useOrg';
import { useUser } from 'hooks/useUser';
import { capitalize, isNumber, sum } from 'lodash-es';
import moment from 'moment';
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Trans } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import jobEstimatorService from 'services/jobEstimatorService';
import {
  MIN_PCT,
  QUANTITY_DEBOUNCE_MILLISECOND,
  SHIPPING_SPECIAL_DELIVERY,
  SHIPPING_SUPPLIER_DELIVERY,
} from 'utils/constants';
import { isModeAdmin, isSourcer, isSupplier } from 'utils/rbac';
import { AddToCartBlockedTooltip } from '../AddToCartBlockedTooltip';
import { ComparisonPress } from '../ComparisonPress';
import { ComparisonShipping } from '../ComparisonShipping';
import { ComparisonMoney } from './ComparisonMoney';
import { ComparisonParcelCost } from './ComparisonParcelCost';
import { ProjectJobAddFromEnum } from '../../../../domains/projectDomain';
import RevxTextWithTooltip from '../../../../components/RevxTextWithTooltip';

const { INDUSTRY_AVERAGE, REVSHOP } =
  JobEstimatorConfirmPaperUpdateParamsColumnType;

interface ISupplierComparison {
  showJobEstimator: {
    list: JobEstimatorComparatorItem[];
    partLabel: JobComparatorPart[];
    svcLabel: JobComparatorSvc[];
    rush?: boolean;
  };
  maxLabel: number;
  clickToggle: (x: string) => void;
  isClickToggle: (x: string) => boolean;
  labelMaxWidth: number;
  boxHeight: number;
  jobEstimator: JobEstimator | undefined;
  choiceSerialNumber: number | undefined;
  updateComparison: (
    jobEstimatorComparatorItem: JobEstimatorComparatorItem
  ) => Promise<void>;
  setJobEstimatorLoading: Dispatch<SetStateAction<boolean>>;
  canAddToCart: boolean;
  cancelUpdateMarkup: () => void;
  updateMarkup: (payload: AdjustMarkupPayload) => Promise<void>;
  setPopoverVisible: Dispatch<SetStateAction<number | false>>;
  popoverVisible: number | false;
  setpopoverType: Dispatch<SetStateAction<PopoverType | undefined>>;
  popoverType: PopoverType | undefined;
  popoverHeight: number | undefined;
  setPopoverHeight: Dispatch<SetStateAction<number | undefined>>;
  revisionId: number;
  spec?: JobSpec;
  addToCArtHandler?: (e: CartLineItem) => void;
}

interface ISupplierComparisonItem extends ISupplierComparison {
  idx: number;
  jobEstimatorItem: JobEstimatorComparatorItem;
  getEligible: (jobEstimatorItem: JobEstimatorComparatorItem) => {
    style: {
      opacity: number;
    };
    show: boolean;
  };
}

const { Option } = Select;

export const SupplierComparisonItem = (props: ISupplierComparison) => {
  const { showJobEstimator, maxLabel } = props;
  // const user = useUser();

  // const flatDeepList = (treeData: any[], arr: any[]) => {
  //   treeData.forEach((item) => {
  //     arr.push(item.org);
  //     if (item.subs && item.subs.length) {
  //       flatDeepList(item.subs, arr);
  //     }
  //   });
  // };

  const getEligible = useMemoizedFn(
    (jobEstimatorItem: JobEstimatorComparatorItem) => {
      return {
        style: {
          opacity:
            jobEstimatorItem.revshopEligible && jobEstimatorItem.averageEligible
              ? 1
              : 0,
        },
        show:
          jobEstimatorItem.revshopEligible && jobEstimatorItem.averageEligible,
      };
    }
  );

  return (
    <>
      {showJobEstimator.list?.map((jobEstimatorItem, idx) => {
        // PRD-471: when there is no industry average price the container still inserts a blank
        // stand-in at index 0 (serialNumber -1) so the later columns keep their index. Skip
        // rendering that empty column - returning null leaves every other column's idx untouched.
        if (jobEstimatorItem.serialNumber === -1) {
          return null;
        }
        return (
          <RevxFlexLayout
            direction="column"
            className={`comparison-drawer-label ${
              showJobEstimator.list.length < 5 ? 'border-right' : ''
            } ${
              idx === showJobEstimator.list.length - 1 ? 'border-none' : ''
            } ${
              !jobEstimatorItem.revshopEligible ||
              !jobEstimatorItem.averageEligible
                ? 'noEligible'
                : ''
            }`}
            style={{
              maxWidth: `${maxLabel}px`,
            }}
            key={`${jobEstimatorItem.comparator.skuId}item${idx}`}
          >
            <SupplierComparisonItemTitle
              idx={idx}
              jobEstimatorItem={jobEstimatorItem}
              getEligible={getEligible}
              {...props}
            />

            <SupplierComparisonItemPart
              idx={idx}
              jobEstimatorItem={jobEstimatorItem}
              getEligible={getEligible}
              {...props}
            />

            <SupplierComparisonItemSvc
              idx={idx}
              jobEstimatorItem={jobEstimatorItem}
              getEligible={getEligible}
              {...props}
            />

            <SupplierComparisonItemGlobalMarkup
              idx={idx}
              jobEstimatorItem={jobEstimatorItem}
              getEligible={getEligible}
              {...props}
            />

            <SupplierComparisonItemSubtotal
              idx={idx}
              jobEstimatorItem={jobEstimatorItem}
              getEligible={getEligible}
              {...props}
            />

            <SupplierComparisonItemSubtotalMarkup
              idx={idx}
              jobEstimatorItem={jobEstimatorItem}
              getEligible={getEligible}
              {...props}
            />

            <SupplierComparisonItemShipping
              idx={idx}
              jobEstimatorItem={jobEstimatorItem}
              getEligible={getEligible}
              {...props}
            />

            <SupplierComparisonItemTotal
              idx={idx}
              jobEstimatorItem={jobEstimatorItem}
              getEligible={getEligible}
              {...props}
            />

            <SupplierComparisonItemAction
              idx={idx}
              jobEstimatorItem={jobEstimatorItem}
              getEligible={getEligible}
              {...props}
            />
          </RevxFlexLayout>
        );
      })}
    </>
  );
};

interface ISupplierComparisonItemTitle extends ISupplierComparisonItem {}

export const SupplierComparisonItemTitle = (
  props: ISupplierComparisonItemTitle
) => {
  const {
    idx,
    jobEstimator,
    jobEstimatorItem,
    choiceSerialNumber,
    updateComparison,
    setJobEstimatorLoading,
    showJobEstimator,
  } = props;
  const [orgId, setOrgId] = useState(jobEstimatorItem.orgId);
  const dispatch = useAppDispatch();
  const shipArrivalDate = (shippings: JobComparatorShipping[]) => {
    let shipDate = shippings[0].readyDate,
      arrialDate = shippings[0].expectedDeliveryDate;
    shippings.forEach((shipping) => {
      if (
        moment(shipping.readyDate, RFQ_SHIPPING_DATE_FORMAT_LOCAL).isBefore(
          moment(shipDate, RFQ_SHIPPING_DATE_FORMAT_LOCAL)
        )
      ) {
        shipDate = shipping.readyDate;
      }

      if (
        moment(
          shipping.expectedDeliveryDate,
          RFQ_SHIPPING_DATE_FORMAT_LOCAL
        ).isAfter(moment(arrialDate, RFQ_SHIPPING_DATE_FORMAT_LOCAL))
      ) {
        arrialDate = shipping.expectedDeliveryDate;
      }
    });
    return {
      shipDate: getFormatDate({
        dateStr: moment(shipDate, RFQ_SHIPPING_DATE_FORMAT_LOCAL),
        formatStr: DEFAULT_DATE_FORMAT,
      }),
      arrialDate: getFormatDate({
        dateStr: moment(arrialDate, RFQ_SHIPPING_DATE_FORMAT_LOCAL),
        formatStr: DEFAULT_DATE_FORMAT,
      }),
    };
  };
  const isRush = useMemo(() => {
    return Boolean(showJobEstimator?.rush);
  }, [showJobEstimator]);
  useEffect(() => {
    setOrgId(jobEstimatorItem.orgId);
  }, [jobEstimatorItem]);

  return (
    <>
      <RevxFlexLayout
        className={`comparison-drawer-rfq comparison-drawer-part-press`}
        justify="justify-center"
        align="top"
        direction="column"
      >
        {idx === 0 ? (
          <>
            <span className="comparison-drawer-rfq-title">
              <Trans i18nKey="platform.sider.industryAvgTitle"></Trans>
            </span>
          </>
        ) : idx === 1 ? (
          <>
            <span className="comparison-drawer-rfq-title">
              <Trans i18nKey="platform.sider.revshopPrice"></Trans>
            </span>
          </>
        ) : (
          <>
            <span className="comparison-drawer-rfq-supplier-title">
              {`Option ${idx - 1}`}
            </span>
            <Select
              className="comparison-drawer-rfq-supplier-select"
              value={orgId}
              getPopupContainer={(e) => {
                return e as HTMLElement;
              }}
              onChange={async (e) => {
                setJobEstimatorLoading(true);
                try {
                  const res = await dispatch.jobEstimator.changeOrg({
                    jobConfigId: jobEstimator?.jobConfigId || '',
                    orgId: e,
                    quantityItemSerialNumber: choiceSerialNumber || 0,
                    comparatorItemSerialNumber: jobEstimatorItem.serialNumber,
                  });
                  updateComparison(res);
                } catch {
                  // the request already reported itself - just let go of the spinner so the
                  // comparison stays usable instead of sitting behind a permanent overlay.
                  setJobEstimatorLoading(false);
                }
              }}
            >
              {jobEstimatorItem.optionOrgs.map((item) => (
                <Option value={item.orgId}>
                  <Tooltip title={item.orgName}>{item.orgName}</Tooltip>
                </Option>
              ))}
            </Select>
          </>
        )}
      </RevxFlexLayout>
      <RevxFlexLayout
        align="center"
        justify="left"
        className={`comparison-drawer-part-service ${
          isRush ? 'comparison-drawer-rush' : ''
        }`}
        hidden={!isRush}
        style={{
          height: '38px',
        }}
      >
        <span>
          {shipArrivalDate(jobEstimatorItem.comparator?.shippings).arrialDate}
        </span>
      </RevxFlexLayout>
    </>
  );
};

export const SupplierComparisonItemPart = (props: ISupplierComparisonItem) => {
  // Both units arrive together; nothing refetches when the buyer switches, so pick here.
  // These arrive as finished labels (`100lb`, `17x11x3 in`), so they are picked, never measured:
  // `display` runs Number() and would turn either of them into undefined.
  const { basisWeight: showBasisWeight } = useMeasurementUnit();
  const {
    idx,
    jobEstimatorItem,
    clickToggle,
    isClickToggle,
    labelMaxWidth,
    jobEstimator,
    choiceSerialNumber,
    updateComparison,
    showJobEstimator,
    setJobEstimatorLoading,
    getEligible,
    spec,
  } = props;
  const dispatch = useAppDispatch();
  const showChangePaperBtn = idx === 0 || idx === 1;
  const showPaperSelection = (data: JobSpec) => {
    dispatch.jobConfig.createConfirmPaper({
      quantitySerialNumber: choiceSerialNumber || 0,
    });
    dispatch.jobEstimator.setState({
      currentComparablePaper: {
        type: idx === 0 ? INDUSTRY_AVERAGE : REVSHOP,
        data,
      },
    });
  };
  return (
    <>
      {jobEstimatorItem.comparator?.parts.map((part, index) => {
        const pressList = part.optionPresses.map((item) => {
          return {
            pressCharge: part.pressCharge,
            pressCode: item.componentCode,
            pressId: item.id,
            pressName: item.componentCodeName,
            name: item.name,
          };
        });
        return (
          <RevxFlexLayout
            direction="column"
            className="comparison-drawer-part"
            key={`jobEstimatorItem${part.skuId} + ${part.paperId} + ${part.pressId}`}
          >
            <RevxFlexLayout
              justify="left"
              className="comparison-drawer-part-label"
              onClick={() => clickToggle(part.skuName)}
            >
              {getEligible(jobEstimatorItem).show && (
                <ComparisonMoney
                  value={sum([
                    part.paperCharge,
                    part.pressCharge,
                    sum(part.svcs.map((svc) => svc.charge)),
                  ])}
                />
              )}
            </RevxFlexLayout>
            <RevxFlexLayout
              align="center"
              justify="left"
              className="comparison-drawer-part-press"
              hidden={!isClickToggle(part.skuName)}
            >
              {getEligible(jobEstimatorItem).show && (
                <Form.Item
                  name={[
                    jobEstimatorItem.serialNumber + '',
                    part.skuId + '',
                    'curSelect',
                  ]}
                >
                  <ComparisonPress
                    onChange={async (e) => {
                      setJobEstimatorLoading(true);
                      try {
                        const res = await dispatch.jobEstimator.adjustPress({
                          jobConfigId: jobEstimator?.jobConfigId || '',
                          quantityItemSerialNumber: choiceSerialNumber || 0,
                          comparatorItemSerialNumber:
                            jobEstimatorItem.serialNumber,
                          pressAdjusts: [
                            {
                              skuId: part.skuId,
                              revxPressId: +e,
                            },
                            ...jobEstimatorItem.comparator.parts.filter(
                              (item) => item.skuId !== part.skuId
                            ),
                          ],
                        });

                        updateComparison(res);
                      } catch {
                        setJobEstimatorLoading(false);
                      }
                    }}
                    optionArr={pressList}
                  />
                </Form.Item>
              )}
            </RevxFlexLayout>
            <RevxFlexLayout
              align="top"
              justify="justify-center"
              direction="column"
              className="comparison-drawer-part-service comparison-drawer-part-service-weight"
              hidden={!isClickToggle(part.skuName)}
              style={
                {
                  // marginBottom: part.svcs.length ? '' : '10px',
                }
              }
            >
              {getEligible(jobEstimatorItem).show && (
                <>
                  <ComparisonMoney value={part.paperCharge} />
                  <span className="comparison-drawer-part-paper-brand">
                    <Typography.Title
                      style={{
                        fontSize: '13px',
                        marginBottom: 0,
                        maxWidth: `${labelMaxWidth - 50}px`,
                      }}
                      ellipsis={{
                        tooltip: `${showBasisWeight(part.paperWeight, {
                          metric: part.paperWeightMetric,
                          withUnit: false,
                        })} ${part.paperCategory
                          .split('_')
                          .map((item) => capitalize(item))
                          .join(' ')}`,
                      }}
                    >
                      {`${showBasisWeight(part.paperWeight, {
                          metric: part.paperWeightMetric,
                          withUnit: false,
                        })} ${part.paperCategory
                        .split('_')
                        .map((item) => capitalize(item))
                        .join(' ')}`}
                    </Typography.Title>
                  </span>
                  <span
                    className="comparison-drawer-default-weight comparison-drawer-part-paper"
                    hidden={!labelMaxWidth}
                  >
                    <Typography.Title
                      style={{
                        fontSize: '13px',
                        marginBottom: 0,
                        maxWidth: `${labelMaxWidth - 50}px`,
                      }}
                      ellipsis={{
                        tooltip: part.paperName || '',
                      }}
                    >
                      {part.paperName || ''}
                    </Typography.Title>
                  </span>
                </>
              )}
            </RevxFlexLayout>
            {spec?.parts[index].paperSearchScope ===
              SearchScopeEnum.MY_PAPER && (
              <RevxFlexLayout
                justify="left"
                className="fixed-height-change-paper"
              >
                {showChangePaperBtn && (
                  <UnderlineText
                    color="blue"
                    onClick={() => {
                      showPaperSelection(spec?.parts[index]);
                    }}
                  >
                    Change paper
                  </UnderlineText>
                )}
              </RevxFlexLayout>
            )}
            {showJobEstimator?.partLabel
              .find((item) => item.skuId === part.skuId)
              ?.svcs.map((item) => {
                const svcObj = part.svcs.find((svc) => item.name === svc.name);
                return (
                  <RevxFlexLayout
                    align="center"
                    justify="left"
                    className="comparison-drawer-part-service comparison-drawer-part-service-weight"
                    hidden={!isClickToggle(part.skuName)}
                    key={`${svcObj?.charge}part${svcObj?.name}`}
                  >
                    {getEligible(jobEstimatorItem).show && (
                      <ComparisonMoney value={svcObj?.charge || 0} />
                    )}
                  </RevxFlexLayout>
                );
              })}
          </RevxFlexLayout>
        );
      })}
    </>
  );
};

export const SupplierComparisonItemSvc = (props: ISupplierComparisonItem) => {
  // Both units arrive together; nothing refetches when the buyer switches, so pick here.
  // These arrive as finished labels (`100lb`, `17x11x3 in`), so they are picked, never measured:
  // `display` runs Number() and would turn either of them into undefined.
  const { sizeFor } = useMeasurementUnit();
  const {
    clickToggle,
    jobEstimatorItem,
    isClickToggle,
    boxHeight,
    showJobEstimator,
    getEligible,
    cancelUpdateMarkup,
    updateMarkup,
    choiceSerialNumber,
    jobEstimator,
  } = props;
  return (
    <RevxFlexLayout direction="column" className="comparison-drawer-part">
      <RevxFlexLayout
        justify="left"
        className="comparison-drawer-part-label"
        onClick={() => clickToggle('Services')}
      >
        {getEligible(jobEstimatorItem).show && (
          <ComparisonMoney
            value={sum([
              ...jobEstimatorItem.comparator?.svcs.map((svc) => svc.charge),
              jobEstimatorItem.comparator?.parcel.charge,
            ])}
          />
        )}
      </RevxFlexLayout>
      {showJobEstimator.svcLabel.map((label, svcIdx) => {
        const svc = jobEstimatorItem.comparator?.svcs.find(
          (item) => item.name === label.name
        );
        return (
          <RevxFlexLayout
            align="center"
            justify="left"
            className="comparison-drawer-wrapper-service comparison-drawer-part-service-weight"
            hidden={!isClickToggle('Services')}
            key={`${svc?.charge}job${svc?.name}`}
            style={{
              marginTop: svcIdx === 0 ? '16px' : '',
            }}
          >
            {getEligible(jobEstimatorItem).show && (
              <>{svc ? <ComparisonMoney value={svc?.charge || 0} /> : 'None'}</>
            )}
          </RevxFlexLayout>
        );
      })}
      <RevxFlexLayout
        align="top"
        justify="justify-start"
        direction="column"
        className="comparison-drawer-wrapper-service comparison-drawer-part-service-parcel comparison-drawer-part-service-weight jobSvcRef"
        hidden={!isClickToggle('Services')}
        style={{
          paddingTop: '10px',
          minHeight: `${boxHeight}px`,
        }}
      >
        {getEligible(jobEstimatorItem).show && (
          <>
            {/* <RevxInputNumber
              className="comparison-drawer-part-pct"
              value={markupValue}
              // disabled={outsourceMarkupDisabled}
              onChange={async (e) => {
                if (markupValue === +e) return;
                setMarkupValue(+e);

                cancelUpdateMarkup();
                updateMarkup({
                  jobConfigId: jobEstimator?.jobConfigId || '',
                  quantityItemSerialNumber: choiceSerialNumber || 0,
                  comparatorItemSerialNumber: jobEstimatorItem.serialNumber,
                  supplierMarkup: +e,
                });
              }}
              prefix="$"
              precision={2}
            /> */}
            <ComparisonParcelCost
              charge={jobEstimatorItem.comparator?.parcel.charge}
              cancelUpdateMarkup={cancelUpdateMarkup}
              updateMarkup={updateMarkup}
              jobConfigId={jobEstimator?.jobConfigId}
              quantityItemSerialNumber={choiceSerialNumber}
              comparatorItemSerialNumber={jobEstimatorItem.serialNumber}
              boxChargeEditable={jobEstimatorItem.boxChargeEditable}
              paperCharge={jobEstimatorItem.comparator?.parts[0].paperCharge}
            />
            <span
              style={{
                fontSize: '11px',
                fontWeight: 400,
              }}
            >
              {sizeFor(
            jobEstimatorItem.comparator?.parcel.dimensions.join(' | '),
            jobEstimatorItem.comparator?.parcel.dimensionsMetric?.join(' | ')
          )}
            </span>
          </>
        )}
      </RevxFlexLayout>
    </RevxFlexLayout>
  );
};

export const SupplierComparisonItemGlobalMarkup = (
  props: ISupplierComparisonItem
) => {
  const {
    jobEstimatorItem,
    idx,
    getEligible,
    cancelUpdateMarkup,
    updateMarkup,
    jobEstimator,
    choiceSerialNumber,
  } = props;
  const [pctValue, setPctValue] = useState<number>();
  const [markupValue, setMarkupValue] = useState<number>();
  useEffect(() => {
    setPctValue(jobEstimatorItem.comparator?.supplierMarkupPct);
    setMarkupValue(jobEstimatorItem.comparator?.supplierMarkup);
  }, [jobEstimatorItem]);
  return (
    <>
      <RevxFlexLayout
        align="center"
        justify="space-between"
        className="comparison-drawer-subtotal "
      >
        {getEligible(jobEstimatorItem).show && (
          <>
            {[0, 1].includes(idx) ? (
              ''
            ) : (
              <>
                <RevxInputNumber
                  className="comparison-drawer-part-pct"
                  // disabled={outsourceMarkupDisabled}
                  onChange={async (e) => {
                    const value = +e < MIN_PCT ? MIN_PCT : +e;
                    if (pctValue === value) return;
                    setPctValue(value);

                    cancelUpdateMarkup();
                    updateMarkup({
                      jobConfigId: jobEstimator?.jobConfigId || '',
                      quantityItemSerialNumber: choiceSerialNumber || 0,
                      comparatorItemSerialNumber: jobEstimatorItem.serialNumber,
                      supplierMarkupPct: value,
                    });
                  }}
                  min={MIN_PCT}
                  value={pctValue}
                  suffix="%"
                  precision={2}
                />
                <RevxInputNumber
                  className="comparison-drawer-part-pct"
                  value={markupValue}
                  // disabled={outsourceMarkupDisabled}
                  onChange={async (e) => {
                    if (markupValue === +e) return;
                    setMarkupValue(+e);

                    cancelUpdateMarkup();
                    updateMarkup({
                      jobConfigId: jobEstimator?.jobConfigId || '',
                      quantityItemSerialNumber: choiceSerialNumber || 0,
                      comparatorItemSerialNumber: jobEstimatorItem.serialNumber,
                      supplierMarkup: +e,
                    });
                  }}
                  prefix="$"
                  precision={2}
                />
              </>
            )}
          </>
        )}
      </RevxFlexLayout>
    </>
  );
};

export const SupplierComparisonItemSubtotal = (
  props: ISupplierComparisonItem
) => {
  const { jobEstimatorItem, getEligible } = props;
  return (
    <RevxFlexLayout justify="left" className="comparison-drawer-subtotal">
      {getEligible(jobEstimatorItem).show && (
        <ComparisonMoney
          value={jobEstimatorItem.comparator?.revxPrintingCharge}
        />
      )}
      {/* {Boolean(+jobEstimatorItem.comparator?.supplierMarkup) ? (
        <div className="comparison-drawer-subtotal-price">
          (
          <ComparisonMoney
            value={jobEstimatorItem.comparator?.supplierMarkup}
          />
          )
        </div>
      ) : (
        <></>
      )} */}
    </RevxFlexLayout>
  );
};

export const SupplierComparisonItemSubtotalMarkup = (
  props: ISupplierComparisonItem
) => {
  const {
    jobEstimatorItem,
    jobEstimator,
    choiceSerialNumber,
    cancelUpdateMarkup,
    updateMarkup,
    getEligible,
    idx,
  } = props;
  const [pctValue, setPctValue] = useState<number>();
  const [markupValue, setMarkupValue] = useState<number>();
  useEffect(() => {
    setPctValue(jobEstimatorItem.comparator?.outsourceMarkupPct);
    setMarkupValue(jobEstimatorItem.comparator?.outsourceMarkup);
  }, [jobEstimatorItem]);

  // const outsourceMarkupDisabled = useMemo(() => {
  //   return Boolean(jobEstimatorItem.orgId ===
  //     (Number(localStorage.getItem('org')) || user?.orgId))
  // }, [user])
  return (
    <RevxFlexLayout
      align="center"
      justify="space-between"
      className="comparison-drawer-wrapper-service"
      style={{
        height: '60px',
        ...getEligible(jobEstimatorItem).style,
      }}
    >
      {[0, 1].includes(idx) ? (
        <>
          <RevxInputNumber
            className="comparison-drawer-part-pct"
            // disabled={outsourceMarkupDisabled}
            onChange={async (e) => {
              const value = +e < MIN_PCT ? MIN_PCT : +e;
              if (pctValue === value) return;
              setPctValue(value);

              cancelUpdateMarkup();
              updateMarkup({
                jobConfigId: jobEstimator?.jobConfigId || '',
                quantityItemSerialNumber: choiceSerialNumber || 0,
                comparatorItemSerialNumber: jobEstimatorItem.serialNumber,
                outsourceMarkupPct: value,
              });
            }}
            min={MIN_PCT}
            value={pctValue}
            suffix="%"
            precision={2}
          />
          <RevxInputNumber
            className="comparison-drawer-part-pct"
            value={markupValue}
            // disabled={outsourceMarkupDisabled}
            onChange={async (e) => {
              if (markupValue === +e) return;
              setMarkupValue(+e);

              cancelUpdateMarkup();
              updateMarkup({
                jobConfigId: jobEstimator?.jobConfigId || '',
                quantityItemSerialNumber: choiceSerialNumber || 0,
                comparatorItemSerialNumber: jobEstimatorItem.serialNumber,
                outsourceMarkup: +e,
              });
            }}
            prefix="$"
            precision={2}
          />
        </>
      ) : (
        <></>
      )}
    </RevxFlexLayout>
  );
};

export const SupplierComparisonItemShipping = (
  props: ISupplierComparisonItem
) => {
  const {
    jobEstimatorItem,
    clickToggle,
    isClickToggle,
    jobEstimator,
    choiceSerialNumber,
    updateComparison,
    setJobEstimatorLoading,
    idx,
    cancelUpdateMarkup,
    updateMarkup,
    popoverType,
    setpopoverType,
    popoverHeight,
    setPopoverHeight,
    popoverVisible,
    setPopoverVisible,
    getEligible,
  } = props;
  const user = useUser();
  const dispatch = useAppDispatch();
  const totalStep = useAppSelector(
    (state) => state.jobConfig.stepInfos?.length
  );
  const [pctValue, setPctValue] = useState<number>();
  const [markupValue, setMarkupValue] = useState<number>();
  const shipRef = useRef(null);
  const shipSize = useSize(shipRef);
  useEffect(() => {
    if (
      popoverVisible === jobEstimatorItem.serialNumber &&
      popoverType === PopoverType.shipping
    ) {
      setPopoverHeight(shipSize?.height ? shipSize?.height + 20 : 0);
    }
    // eslint-disable-next-line
  }, [shipSize]);
  useEffect(() => {
    setPctValue(jobEstimatorItem.comparator?.outsourceShippingMarkupPct);
    setMarkupValue(jobEstimatorItem.comparator?.outsourceShippingMarkup);
  }, [jobEstimatorItem]);
  const shipArrivalDate = (shippings: JobComparatorShipping[]) => {
    let shipDate = shippings[0].readyDate,
      arrialDate = shippings[0].deliveryDate;
    shippings.forEach((shipping) => {
      if (
        moment(shipping.readyDate, RFQ_SHIPPING_DATE_FORMAT_LOCAL).isBefore(
          moment(shipDate, RFQ_SHIPPING_DATE_FORMAT_LOCAL)
        )
      ) {
        shipDate = shipping.readyDate;
      }

      if (
        moment(shipping.deliveryDate, RFQ_SHIPPING_DATE_FORMAT_LOCAL).isAfter(
          moment(arrialDate, RFQ_SHIPPING_DATE_FORMAT_LOCAL)
        )
      ) {
        arrialDate = shipping.deliveryDate;
      }
    });
    return {
      shipDate: getFormatDate({
        dateStr: moment(shipDate, RFQ_SHIPPING_DATE_FORMAT_LOCAL),
        formatStr: DEFAULT_DATE_FORMAT,
      }),
      arrialDate: getFormatDate({
        dateStr: moment(arrialDate, RFQ_SHIPPING_DATE_FORMAT_LOCAL),
        formatStr: DEFAULT_DATE_FORMAT,
      }),
    };
  };
  const { run: changeShipping, cancel: cancelChangeShipping } = useDebounceFn(
    async (payload: Partial<JobRFQAdjustPayload>) => {
      setJobEstimatorLoading(true);
      try {
        const res = await dispatch.jobEstimator.changeShipping({
          ...payload,
          comparatorItemSerialNumber: jobEstimatorItem.serialNumber,
        });
        updateComparison(res);
        // setPopoverVisible(false);
        return 'success';
      } catch {
        return 'error';
      } finally {
        setJobEstimatorLoading(false);
      }
    },
    { wait: QUANTITY_DEBOUNCE_MILLISECOND }
  );
  const freeShipping = useMemo(() => {
    return Boolean(
      !jobEstimatorItem.comparator?.shippings.find((item) => !item.freeShipping)
    );
  }, [jobEstimatorItem]);

  const isSpecialDelivery = useMemo(() => {
    return Boolean(
      jobEstimatorItem.comparator.shippings?.find(
        (item) =>
          item.shippingInfo.carrierService === SHIPPING_SPECIAL_DELIVERY ||
          item.shippingInfo.carrierService === SHIPPING_SUPPLIER_DELIVERY
      )
    );
  }, [jobEstimatorItem.comparator.shippings]);

  const isSupplierShipping = useMemo(() => {
    return Boolean(
      jobEstimatorItem.comparator.shippings?.find(
        (item) =>
          item.shippingInfo.accountType === ShippingAccountTypeEnum.SUPPLIER
      )
    );
  }, [jobEstimatorItem.comparator.shippings]);

  const readyDate = useMemo(() => {
    if (isSpecialDelivery || isSupplierShipping) {
      if (jobEstimatorItem.comparator.shippings?.[0]?.readyDate) {
        return moment(
          jobEstimatorItem.comparator.shippings?.[0]?.readyDate,
          moment.ISO_8601
        );
      } else {
        return undefined;
      }
    } else {
      return moment(
        jobEstimatorItem.comparator.shippings?.[0]?.readyDate ||
          jobEstimatorItem.comparator.shippings?.[0]?.suggestReadyDate,
        moment.ISO_8601
      );
    }
  }, [
    isSpecialDelivery,
    isSupplierShipping,
    jobEstimatorItem.comparator.shippings,
  ]);
  const popoverShow = useMemo(() => {
    return (
      popoverVisible === jobEstimatorItem.serialNumber &&
      popoverType === PopoverType.shipping
    );
  }, [popoverVisible, jobEstimatorItem.serialNumber, popoverType]);
  const popverStyle = useMemo(() => {
    return isNumber(popoverVisible) && popoverType === PopoverType.shipping;
  }, [popoverVisible, popoverType]);
  return (
    <RevxFlexLayout direction="column" className="comparison-drawer-wrapper">
      <RevxFlexLayout
        justify="left"
        className="comparison-drawer-part-label comparison-drawer-wrapper-label"
        onClick={() => clickToggle('Shipping Total')}
      >
        {getEligible(jobEstimatorItem).show && (
          <>
            <ComparisonMoney
              hidden={freeShipping}
              value={jobEstimatorItem.comparator?.freightCharge}
            />
            <span
              style={{
                fontSize: '12px',
              }}
              hidden={!freeShipping}
            >
              <Trans i18nKey="jobConfig.stepViewQuote.freeShipping"></Trans>
            </span>
          </>
        )}
      </RevxFlexLayout>

      <RevxFlexLayout
        align="center"
        justify="left"
        className="comparison-drawer-part-service"
        hidden={!isClickToggle('Shipping Total')}
      >
        {getEligible(jobEstimatorItem).show && (
          <DatePicker
            disabled={idx === 1}
            allowClear={false}
            size="small"
            defaultValue={readyDate}
            value={readyDate}
            format={DEFAULT_DATE_FORMAT}
            disabledDate={
              (d) =>
                d &&
                Boolean(
                  jobEstimatorItem.comparator.shippings?.[0].orderDate &&
                    d <
                      moment(
                        jobEstimatorItem.comparator.shippings?.[0].orderDate
                      )
                ) /* ||
                Boolean(
                  jobEstimatorItem.comparator.shippings?.[0]
                    .expectedDeliveryDate &&
                    d >
                      moment(
                        jobEstimatorItem.comparator.shippings?.[0]
                          .expectedDeliveryDate
                      )
                )*/
            }
            onChange={(e) => {
              cancelChangeShipping();
              changeShipping({
                id: jobEstimator?.jobConfigId || 0,
                orgId: jobEstimatorItem?.comparator.orgId,
                qtySerialNumber: choiceSerialNumber || 0,
                body: {
                  readyDate: moment(e).format(RFQ_SHIPPING_DATE_FORMAT_LOCAL),
                  shippingAdjusts: (
                    jobEstimatorItem.comparator.shippings || []
                  ).map((item) => {
                    return {
                      serialNumber: item.serialNumber,
                      charge: item.freightCost,
                    };
                  }),
                },
              });
            }}
          />
        )}
      </RevxFlexLayout>

      <RevxFlexLayout
        align="center"
        justify="left"
        className="comparison-drawer-part-service"
        hidden={!isClickToggle('Shipping Total')}
      >
        {getEligible(jobEstimatorItem).show && (
          <Popover
            // placement="right"
            overlayClassName="rfq-shipping-popover rfqResponse-drawer-part-shipping jobConfig-popover"
            getPopupContainer={(e) =>
              document.querySelector(
                '.comparison-drawer-popover'
              ) as HTMLElement
            }
            visible={popoverShow}
            onVisibleChange={(e) => {
              setPopoverVisible(
                e ? jobEstimatorItem?.serialNumber || 0 : false
              );
              if (e) {
                setpopoverType(PopoverType.shipping);
              }
            }}
            content={
              <div ref={shipRef}>
                <RevxSupplierShippingMethodTable
                  disabled={idx === 0}
                  isJobConfig={true}
                  quantity={
                    jobEstimator?.quantityItems.find(
                      (item) => item.serialNumber === choiceSerialNumber
                    )?.quantity || 0
                  }
                  quantityIndex={choiceSerialNumber || 0}
                  shippingItems={jobEstimatorItem?.comparator.shippings}
                  rfqId={jobEstimator?.jobConfigId}
                  orgId={jobEstimatorItem?.comparator.orgId}
                  qtySerialNumber={choiceSerialNumber || 0}
                  shipmentMethodChangeHandler={(payload) => {
                    cancelChangeShipping();
                    return changeShipping(payload);
                  }}
                />
              </div>
            }
            trigger="click"
          >
            <div
              className="comparison-drawer-part-shipping"
              style={{ whiteSpace: 'nowrap' }}
            >
              {jobEstimatorItem.comparator?.shippings.length !== 1 ? (
                <UnderlineText color="blue">{`Split Shipment (${jobEstimatorItem.comparator?.shippings.length})`}</UnderlineText>
              ) : (
                <UnderlineText color="blue">
                  <RevxTextWithTooltip
                    text={
                      jobEstimatorItem.comparator?.shippings[0].shippingInfo
                        .carrierService
                    }
                    maxWidth={200}
                  />
                </UnderlineText>
              )}
              <div
                hidden={!popoverShow}
                className="comparison-drawer-part-shipping-arrow"
              ></div>
            </div>
          </Popover>
        )}
      </RevxFlexLayout>

      <RevxFlexLayout
        align="center"
        justify="left"
        className="comparison-drawer-part-service"
        // hidden={!isClickToggle('Shipping Total')}
        hidden
      >
        {jobEstimatorItem.comparator?.shippings.length === 1 && false ? (
          <ComparisonShipping
            value={{
              carrierService:
                jobEstimatorItem.comparator?.shippings[0].shippingInfo
                  .carrierService,
              carrierType:
                jobEstimatorItem.comparator?.shippings[0].shippingInfo
                  .carrierType,
              shipmentType:
                jobEstimatorItem.comparator?.shippings[0].shippingInfo
                  .shipmentType,
              charge:
                jobEstimatorItem.comparator?.shippings[0]
                  .supplierShippingCharge,
            }}
            onChange={async (e: JobComparatorShippingCarrierServices) => {
              // setJobEstimatorLoading(true);
              // const res = await dispatch.jobEstimator.changeShipping({
              //   jobConfigId: jobEstimator?.jobConfigId || '',
              //   quantityItemSerialNumber: choiceSerialNumber || 0,
              //   comparatorItemSerialNumber: jobEstimatorItem.serialNumber,
              //   serialNumber:
              //     jobEstimatorItem.comparator?.shippings[0].serialNumber,
              //   carrierService: e.serviceName,
              //   carrierType: e.carrierType,
              //   shipmentType: e.shipmentType,
              // });
              // updateComparison(res);
            }}
            optionArr={[]}
            // optionArr={
            // jobEstimatorItem.comparator?.shippings[0].shippingInfo.carrierServices
            // }
          />
        ) : (
          <>
            {getEligible(jobEstimatorItem).show && (
              <Popconfirm
                placement="top"
                title={
                  <Trans i18nKey="jobConfig.stepComparison.popconfirm"></Trans>
                }
                icon={null}
                onConfirm={() => {
                  if (user && (isSourcer(user) || isSupplier(user))) {
                    dispatch.jobConfig.goToStep(totalStep - 3);
                  } else {
                    dispatch.jobConfig.goToStep(totalStep - 2);
                  }
                }}
                overlayClassName="comparison-drawer-body-confirm"
                getPopupContainer={(e) => {
                  return e as HTMLElement;
                }}
                okText={<Trans i18nKey="common.confirm"></Trans>}
                cancelText={<Trans i18nKey="common.cancel"></Trans>}
              >
                {jobEstimatorItem.comparator?.shippings.length !== 1 ? (
                  <UnderlineText color="blue">{`Split Shipment (${jobEstimatorItem.comparator?.shippings.length})`}</UnderlineText>
                ) : (
                  <UnderlineText color="blue">
                    {
                      jobEstimatorItem.comparator?.shippings[0].shippingInfo
                        .carrierService
                    }
                  </UnderlineText>
                )}
              </Popconfirm>
            )}
          </>
        )}
      </RevxFlexLayout>

      <RevxFlexLayout
        align="center"
        justify="left"
        className="comparison-drawer-part-service"
        hidden={!isClickToggle('Shipping Total')}
        style={{
          marginTop: popverStyle ? (popoverHeight || 0)! + 10 : 0,
          ...getEligible(jobEstimatorItem).style,
        }}
      >
        <RevxInputNumber
          className="comparison-drawer-part-pct"
          disabled={!jobEstimatorItem.comparator?.freightCharge}
          onChange={async (e) => {
            const value = +e < MIN_PCT ? MIN_PCT : +e;
            if (pctValue === value) return;
            setPctValue(value);

            cancelUpdateMarkup();
            updateMarkup({
              jobConfigId: jobEstimator?.jobConfigId || '',
              quantityItemSerialNumber: choiceSerialNumber || 0,
              comparatorItemSerialNumber: jobEstimatorItem.serialNumber,
              outsourceShippingMarkupPct: value,
            });
          }}
          min={MIN_PCT}
          value={pctValue}
          suffix="%"
          precision={2}
        />
        <RevxInputNumber
          className="comparison-drawer-part-pct"
          value={markupValue}
          disabled={!jobEstimatorItem.comparator?.freightCharge}
          onChange={async (e) => {
            if (markupValue === +e) return;
            setMarkupValue(+e);

            cancelUpdateMarkup();
            updateMarkup({
              jobConfigId: jobEstimator?.jobConfigId || '',
              quantityItemSerialNumber: choiceSerialNumber || 0,
              comparatorItemSerialNumber: jobEstimatorItem.serialNumber,
              outsourceShippingMarkup: +e,
            });
          }}
          prefix="$"
          precision={2}
        />
      </RevxFlexLayout>

      <RevxFlexLayout
        align="center"
        justify="left"
        className="comparison-drawer-part-service"
        hidden={!isClickToggle('Shipping Total')}
        style={{
          height: '38px',
        }}
      >
        {getEligible(jobEstimatorItem).show && (
          <span>
            {shipArrivalDate(jobEstimatorItem.comparator?.shippings).shipDate}
          </span>
        )}
      </RevxFlexLayout>

      <RevxFlexLayout
        align="center"
        justify="left"
        className="comparison-drawer-part-service"
        hidden={!isClickToggle('Shipping Total')}
        style={{
          height: '38px',
        }}
      >
        {getEligible(jobEstimatorItem).show && (
          <span>
            {shipArrivalDate(jobEstimatorItem.comparator?.shippings).arrialDate}
          </span>
        )}
      </RevxFlexLayout>
    </RevxFlexLayout>
  );
};

export const SupplierComparisonItemTotal = (props: ISupplierComparisonItem) => {
  const { jobEstimatorItem, jobEstimator, choiceSerialNumber, getEligible } =
    props;
  const orgId = useOrgId();
  return (
    <RevxFlexLayout
      align="center"
      justify="space-between"
      className="comparison-drawer-part-label comparison-drawer-wrapper-label comparison-drawer-part-label-total"
    >
      {getEligible(jobEstimatorItem).show && (
        <>
          <ComparisonMoney value={jobEstimatorItem.comparator?.totalCharge} />
          <RevxFlexLayout align="center">
            {/* {jobEstimatorItem.orgId === orgId && (
              <div
                style={{ marginRight: 8, cursor: 'pointer' }}
                onClick={() => {
                  jobEstimatorService.viewValidatePdf({
                    jobConfigId: jobEstimator?.jobConfigId || '',
                    quantityItemSerialNumber: choiceSerialNumber || 0,
                    comparatorItemSerialNumber: jobEstimatorItem.serialNumber,
                  });
                }}
              >
                <CalculatorOutlined style={{ fontSize: '18px' }} />
              </div>
            )} */}
            <div
              hidden={isModeAdmin() && jobEstimatorItem.orgId !== orgId}
              style={{ cursor: 'pointer' }}
              onClick={() => {
                jobEstimatorService.viewValidatePdf({
                  jobConfigId: jobEstimator?.jobConfigId || '',
                  quantityItemSerialNumber: choiceSerialNumber || 0,
                  comparatorItemSerialNumber: jobEstimatorItem.serialNumber,
                });
              }}
            >
              <CalculatorOutlined style={{ fontSize: '18px' }} />
            </div>
          </RevxFlexLayout>
        </>
      )}
    </RevxFlexLayout>
  );
};

export const SupplierComparisonItemAction = (
  props: ISupplierComparisonItem
) => {
  // The wizard's unit, so a job configured in millimetres prints its quote and PO in millimetres.
  const { unit } = useMeasurementUnit();
  const {
    jobEstimator,
    choiceSerialNumber,
    jobEstimatorItem,
    setJobEstimatorLoading,
    canAddToCart,
    idx,
    getEligible,
    revisionId,
    addToCArtHandler,
  } = props;
  const dispatch = useAppDispatch();
  const history = useHistory();
  const isJobRevision = useJobRevision(props);
  const org = useOrg();
  const uiStatusFilter = useAppSelector(
    (state) => state.workspace.uiStatusFilter
  );
  const jobProjectId = useAppSelector((state) => state.project.jobProjectId);
  /**
   * PRD-471: the comparator says per column whether Add to Cart is blocked, which is what the
   * sourcer needs here - the column can be switched to Industry Average long before the job config
   * knows about it, and until then `industryAverageAddToCartBlocked` on the job config is false.
   * That job config flag stays as a fallback for estimators cached before the column carried one.
   */
  const addToCartBlocked = useAppSelector(
    (state) =>
      jobEstimatorItem.addToCartDisabled ||
      (Boolean(state.jobConfig.configItem?.industryAverageAddToCartBlocked) &&
        isIndustryAvgOrg({ id: jobEstimatorItem.orgId }))
  );
  return (
    <RevxFlexLayout
      align="top"
      justify="left"
      direction="column"
      className="comparison-drawer-body-action"
    >
      <div hidden={idx === 0}>
        {getEligible(jobEstimatorItem).show ? (
          <a
            href={jobEstimatorService.viewQuotePdf({
              jobConfigId: jobEstimator?.jobConfigId || '',
              quantityItemSerialNumber: choiceSerialNumber || 0,
              comparatorItemSerialNumber: jobEstimatorItem.serialNumber,
              measurementUnit: unit,
            })}
            target="_blank"
            rel="noreferrer"
          >
            <UnderlineText color="blue">
              <Trans i18nKey="jobConfig.viewQuote"></Trans>
            </UnderlineText>
          </a>
        ) : (
          <UnderlineText color="gray">
            <Trans i18nKey="jobConfig.viewQuote"></Trans>
          </UnderlineText>
        )}
      </div>
      <div
        hidden={
          idx === 0 ||
          (idx === 1 &&
            jobEstimator?.quantityItems[choiceSerialNumber || 0]
              .revshopEligible)
        }
      >
        {getEligible(jobEstimatorItem).show ? (
          <a
            href={jobEstimatorService.viewPOPdf({
              jobConfigId: jobEstimator?.jobConfigId || '',
              quantityItemSerialNumber: choiceSerialNumber || 0,
              comparatorItemSerialNumber: jobEstimatorItem.serialNumber,
              measurementUnit: unit,
            })}
            target="_blank"
            rel="noreferrer"
          >
            <UnderlineText color="blue">
              <Trans i18nKey="jobConfig.viewPo"></Trans>
            </UnderlineText>
          </a>
        ) : (
          <UnderlineText color="gray">
            <Trans i18nKey="jobConfig.viewPo"></Trans>
          </UnderlineText>
        )}
      </div>
      {/* PRD-471: the button stays in place so the column keeps its layout, but a blocked
          Industry Average price cannot be checked out - the server refuses the call, and the
          tooltip says so instead of leaving the sourcer guessing at a grey button. */}
      <AddToCartBlockedTooltip blocked={addToCartBlocked}>
        <RevxButton
          type="primary"
          className="comparison-drawer-body-action-button"
          hidden={(!canAddToCart && idx === 1) || idx === 0 || isJobRevision}
          disabled={addToCartBlocked || !getEligible(jobEstimatorItem).show}
          onClick={async () => {
            const doAddToCart = async () => {
              setJobEstimatorLoading(true);
              dispatch.jobConfig.setRevxSpinloading(true);
              try {
                const cartLineItem = await dispatch.jobEstimator.addToCart({
                  jobConfigId: jobEstimator?.jobConfigId || '',
                  quantityItemSerialNumber: choiceSerialNumber || 0,
                  comparatorItemSerialNumber: jobEstimatorItem.serialNumber,
                });
                if (jobProjectId) {
                  await dispatch.project.jobProjectAddJob({
                    projectId: jobProjectId,
                    addFrom: ProjectJobAddFromEnum.SHOP_GALLERY,
                    jobId: cartLineItem.jobId,
                  });
                  dispatch.project.reloadProjectJobList();
                }
                dispatch.jobConfig.setRevxSpinloading(false);
                if (typeof addToCArtHandler === 'function') {
                  addToCArtHandler(cartLineItem);
                } else {
                  history.push(routesMap.shoppingCart.path);
                }
              } catch {
                dispatch.jobConfig.setRevxSpinloading(false);
              } finally {
                // whatever the checkout rejected, the sourcer has to be able to go on
                // comparing - never leave the comparison behind its loading overlay.
                setJobEstimatorLoading(false);
              }
            };
            if (org && org.id !== jobEstimatorItem.orgId) {
              Modal.confirm({
                title: 'Confirm',
                content: `Are you sure you want to send this job to ${jobEstimatorItem.orgName}?`,
                okText: 'Yes',
                cancelText: 'No',
                onOk: doAddToCart,
              });
            } else {
              doAddToCart();
            }
          }}
        >
          <Trans
            i18nKey={
              jobProjectId
                ? 'gallery.skuList.addToProject'
                : 'jobConfig.addToCart'
            }
          ></Trans>
        </RevxButton>
      </AddToCartBlockedTooltip>
      <RevxButton
        type="primary"
        className="comparison-drawer-body-action-button"
        hidden={idx === 1 || idx === 0 || !isJobRevision}
        disabled={!getEligible(jobEstimatorItem).show}
        onClick={async () => {
          try {
            await dispatch.jobConfig.completeFromEstimator({
              revisionId,
              quantityItemSerialNumber: choiceSerialNumber || 0,
              comparatorItemSerialNumber: jobEstimatorItem.serialNumber,
            });
          } catch {}
          history.push(`/workspace/${uiStatusFilter}`);
        }}
      >
        <Trans i18nKey="jobConfig.updateJob"></Trans>
      </RevxButton>
    </RevxFlexLayout>
  );
};
